import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  ClockIcon,
  PencilIcon,
  LockClosedIcon,
  KeyIcon,
  CheckCircleIcon,
  XCircleIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useToast, ToastContainer } from '../common/Toast';
import Dialog from '../common/Dialog';
import AssignSiteModal from './AssignSiteModal';
import userService, { UserDetail as UserDetailType, Role } from '../../services/users';
import siteService from '../../services/sites';
import { userAssignmentsService } from '../../services/userAssignments';

type TabType = 'profil' | 'connexions' | 'actions';

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { toasts, showSuccess, showError } = useToast();
  
  const [user, setUser] = useState<UserDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('profil');
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [showAssignSiteModal, setShowAssignSiteModal] = useState(false);
  const [availableSites, setAvailableSites] = useState<Array<{id: number, name: string, code: string}>>([]);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [assigningSite, setAssigningSite] = useState(false);
  const [userAssignments, setUserAssignments] = useState<any[]>([]);


  useEffect(() => {
    if (userId) {
      loadUserData();
    } else {
      console.log('⚠️ userId manquant dans les paramètres');
    }
  }, [userId]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      
      // Récupérer les données utilisateur depuis le backend
      const userData = await userService.getUserById(userId!);
      setUser(userData);
      
      // Récupérer les assignations de sites séparément
      console.log('🔍 Récupération des assignations pour l\'utilisateur:', userId);
      try {
        const assignments = await userAssignmentsService.getAssignments(userId!);
        console.log('✅ Assignations récupérées:', assignments);
        setUserAssignments(assignments);
      } catch (assignmentError) {
        console.warn('⚠️ Erreur lors de la récupération des assignations:', assignmentError);
        setUserAssignments([]);
      }
      
    } catch (error: any) {
      console.error('❌ Erreur lors du chargement de l\'utilisateur:', error);
      let errorMessage = 'Impossible de charger les données de l\'utilisateur';
      if (error.response?.status === 404) {
        errorMessage = 'Utilisateur non trouvé (404)';
      } else if (error.response?.status === 401) {
        errorMessage = 'Non autorisé - Veuillez vous reconnecter (401)';
      } else if (error.response?.status === 500) {
        errorMessage = 'Erreur serveur (500)';
      } else if (error.message) {
        errorMessage = `${errorMessage}: ${error.message}`;
      }
      
      showError('Erreur', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBlockUser = async () => {
    if (!user || !blockReason.trim()) return;
    
    try {
      
      // Appel API pour bloquer l'utilisateur
      await userService.blockUser(user.id, blockReason);
      showSuccess('Succès', 'Utilisateur bloqué avec succès');
      setShowBlockDialog(false);
      setBlockReason('');
      
      // Recharger les données
      await loadUserData();
    } catch (error: any) {
      console.error('❌ Erreur lors du blocage:', error);
      showError('Erreur', `Impossible de bloquer l'utilisateur: ${error.message || 'Erreur inconnue'}`);
    }
  };

  const handleUnblockUser = async () => {
    if (!user) return;
    
    try {
      
      await userService.unblockUser(user.id, 'Déblocage administrateur');
      showSuccess('Succès', 'Utilisateur débloqué avec succès');
      
      // Recharger les données
      await loadUserData();
    } catch (error: any) {
      console.error('❌ Erreur lors du déblocage:', error);
      showError('Erreur', `Impossible de débloquer l'utilisateur: ${error.message || 'Erreur inconnue'}`);
    }
  };

    // Fonction pour charger les sites et rôles disponibles
  const loadAvailableSitesAndRoles = async () => {
    try {
      setAssigningSite(true);
     
      // Charger les sites disponibles (exclure ceux déjà assignés)
      const sitesResponse = await siteService.getSites();
      console.log('🔍 Sites chargés:', sitesResponse);
      
      const assignedSiteIds = user?.site_roles?.map(sr => Number(sr.site.id)) || [];
      console.log('🔍 Sites déjà assignés:', assignedSiteIds);
      
      const availableSites = sitesResponse.filter(site => !assignedSiteIds.includes(site.id));
      console.log('🔍 Sites disponibles:', availableSites);
      
      setAvailableSites(availableSites);
      
      // Charger les rôles disponibles
      console.log('🔍 Chargement des rôles...');
      const rolesResponse = await userService.getRoles();
      console.log('🔍 Réponse des rôles:', rolesResponse);
      
             // Extraire les rôles de la réponse paginée
       let roles: Role[] = [];
       if (rolesResponse && typeof rolesResponse === 'object') {
         if (Array.isArray(rolesResponse)) {
           // Si c'est déjà un tableau (fallback)
           roles = rolesResponse;
         } else if ('results' in rolesResponse && Array.isArray(rolesResponse.results)) {
           // Si c'est une réponse paginée
           roles = rolesResponse.results;
         } else {
           console.warn('⚠️ Structure de réponse inattendue:', rolesResponse);
         }
       }
       
       // Filtrer le rôle SUPERADMIN
       roles = roles.filter(role => role.name !== 'SUPERADMIN');
      
      console.log('🔍 Rôles extraits:', roles);
      console.log('🔍 Nombre de rôles:', roles.length);
      
      if (roles.length > 0) {
        console.log('✅ Rôles valides, mise à jour du state avec', roles.length, 'rôles');
        setAvailableRoles(roles);
      } else {
        console.warn('⚠️ Aucun rôle trouvé');
        setAvailableRoles([]);
      }
      
    } catch (error: any) {
      console.error('❌ Erreur lors du chargement des sites/rôles:', error);
      showError('Erreur', 'Impossible de charger les sites et rôles disponibles');
    } finally {
      setAssigningSite(false);
    }
  };

  // Fonction pour gérer l'assignation d'un site
  const handleAssignSite = async (siteId: number, roleId: string, sessionType: 'PERMANENTE' | 'TEMPORAIRE', isDefault: boolean, startDate?: string, endDate?: string) => {
    if (!userId) {
      alert('ID utilisateur non défini');
      return;
    }

    try {
      setLoading(true);
      
      // Utiliser le nouveau service unifié
      await userAssignmentsService.createAssignment(userId, {
        user: parseInt(userId),
        site: siteId,
        role: parseInt(roleId),
        session_type: sessionType,
        starts_at: startDate,
        ends_at: endDate,
        is_default: isDefault
      });

      // Recharger les données utilisateur et les assignations
      await loadUserData();
      
      // Fermer le modal
      setShowAssignSiteModal(false);
      
      // Afficher un message de succès
      alert('Site assigné avec succès !');
    } catch (error: any) {
      console.error('❌ Erreur lors de l\'assignation du site:', error);
      alert(`Erreur lors de l'assignation: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };



  // Fonction pour ouvrir le modal d'assignation
  const openAssignSiteModal = async () => {
    try {
      // Charger d'abord les données avant d'ouvrir le modal
      await loadAvailableSitesAndRoles();
      // Ouvrir le modal seulement après avoir chargé les données
      setShowAssignSiteModal(true);
    } catch (error: any) {
      console.error('❌ Erreur lors de l\'ouverture du modal:', error);
      showError('Erreur', 'Impossible de charger les données nécessaires pour l\'assignation');
    }
  };


  // Fonction pour formater les données des sites avec rôles
  const getFormattedSites = () => {
    console.log('🔍 Formatage des sites - Assignations disponibles:', userAssignments);
    
    // Utiliser les assignations récupérées séparément
    if (!userAssignments || !Array.isArray(userAssignments) || userAssignments.length === 0) {
      console.log('⚠️ Aucune assignation disponible');
      return [];
    }
    
    try {
      const formatted = userAssignments
        .filter((assignment) => assignment.is_active) // Filtrer seulement les assignations actives
        .map((assignment) => {
          const formattedSite = {
            id: assignment.site,
            name: assignment.site_name || 'Site non défini',
            code: assignment.site_code || 'N/A',
            role: assignment.role_label || 'Rôle non défini',
            assigned_at: assignment.created_at || new Date().toISOString(),
            is_default: assignment.is_default || false
          };
          
          return formattedSite;
        });
      
      console.log('✅ Sites formatés:', formatted);
      return formatted;
    } catch (error) {
      console.error('❌ Erreur lors du formatage des sites:', error);
      return [];
    }
  };

  // Fonction pour formater l'historique des connexions
  const getFormattedConnections = () => {
    if (!user?.site_sessions) return [];
    
    return user.site_sessions
      .filter(session => session.is_active)
      .map(session => ({
        id: session.id,
        date: session.starts_at || new Date().toISOString(),
        ip: 'N/A', // L'IP n'est pas disponible dans l'interface actuelle
        user_agent: 'N/A', // Le user agent n'est pas disponible
        status: 'success' as const
      }));
  };

  // Fonction pour formater l'historique des actions
  const getFormattedActions = () => {
    if (!user?.audit_logs) return [];
    
    return user.audit_logs.map(log => ({
      id: log.id,
      action: log.action || 'Action non spécifiée',
      details: log.payload_json ? JSON.stringify(log.payload_json) : 'Aucun détail',
      date: log.created_at || new Date().toISOString(),
      ip: 'N/A' // L'IP n'est pas disponible dans l'interface actuelle
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des données utilisateur...</p>
          <p className="text-sm text-gray-500 mt-2">ID: {userId}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Utilisateur non trouvé</h2>
          <Link
            to="/administration/users"
            className="text-orange-600 hover:text-orange-700"
          >
            Retour à la liste
          </Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'profil', label: 'Profil', icon: <UserIcon className="h-4 w-4" /> },
    { id: 'connexions', label: 'Historique connexions', icon: <ClockIcon className="h-4 w-4" /> },
    { id: 'actions', label: 'Historique actions', icon: <DocumentTextIcon className="h-4 w-4" /> }
  ];

  // Données formatées
  const formattedSites = getFormattedSites();
 
  
  const formattedConnections = getFormattedConnections();
  const formattedActions = getFormattedActions();

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link
                to="/administration/users"
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </Link>
              <UserIcon className="w-8 h-8 text-orange-500 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{user.name}</h1>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <span className="font-medium">@{user.username}</span>
                  {user.email && <span>• {user.email}</span>}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                !user.is_blocked 
                  ? 'bg-green-100 text-green-800 border border-green-200' 
                  : 'bg-red-100 text-red-800 border border-red-200'
              }`}>
                {!user.is_blocked ? (
                  <>
                    <CheckCircleIcon className="h-3 w-3 mr-1" />
                    Actif
                  </>
                ) : (
                  <>
                    <XCircleIcon className="h-3 w-3 mr-1" />
                    Bloqué
                  </>
                )}
              </span>
              
              {user.is_blocked ? (
                <button
                  onClick={handleUnblockUser}
                  className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium bg-green-600 text-white hover:bg-green-700 shadow-sm hover:shadow transition-all"
                >
                  <KeyIcon className="h-4 w-4" />
                  Débloquer
                </button>
              ) : (
                <button
                  onClick={() => setShowBlockDialog(true)}
                  className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium bg-red-600 text-white hover:bg-red-700 shadow-sm hover:shadow transition-all"
                >
                  <LockClosedIcon className="h-4 w-4" />
                  Bloquer
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation des onglets */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Contenu des onglets */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'profil' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Photo de profil */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="text-center">
                    <div className="w-32 h-32 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <UserIcon className="h-16 w-16 text-orange-500" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{user.name}</h3>
                    <p className="text-sm text-gray-500">@{user.username}</p>
                    <button className="mt-3 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors">
                      <PencilIcon className="h-4 w-4" />
                      Modifier la photo
                    </button>
                  </div>
                </div>
              </div>

              {/* Informations du profil */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Informations personnelles</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet</label>
                      <p className="text-sm text-gray-900">{user.name}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom d'utilisateur</label>
                      <p className="text-sm text-gray-900">@{user.username}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <div className="flex items-center">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <p className="text-sm text-gray-900">{user.email || 'Non renseigné'}</p>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                      <div className="flex items-center">
                        <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <p className="text-sm text-gray-900">{user.phone || 'Non renseigné'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 mt-6 pt-6">
                    <h4 className="text-md font-medium text-gray-900 mb-3">Informations système</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          !user.is_blocked 
                            ? 'bg-green-100 text-green-800 border border-green-200' 
                            : 'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                          {!user.is_blocked ? 'Actif' : 'Bloqué'}
                        </span>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Compte créé le</label>
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <p className="text-sm text-gray-900">
                            {new Date(user.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Dernière connexion</label>
                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <p className="text-sm text-gray-900">
                            {user.site_sessions && user.site_sessions.length > 0 
                              ? new Date(user.site_sessions[0].starts_at || new Date()).toLocaleDateString('fr-FR')
                              : 'Jamais connecté'}
                          </p>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Dernière modification</label>
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <p className="text-sm text-gray-900">
                            {new Date(user.updated_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

                         {/* Carte séparée pour les sites assignés */}
             <div className="mt-6">
               <div className="bg-white rounded-xl shadow-sm p-6">
                 <div className="flex items-center justify-between mb-4">
                   <h3 className="text-lg font-medium text-gray-900">Sites assignés et rôles</h3>
                   <button 
                     onClick={openAssignSiteModal}
                     className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors"
                   >
                     <PlusIcon className="h-4 w-4" />
                     Assigner un site
                   </button>
                 </div>
                 
                 
                 
                 {formattedSites.length > 0 ? (
                   <div className="space-y-3">
                     {formattedSites.map((site) => (
                       <div key={site.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                         <div className="flex items-center">
                           <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-3" />
                           <div>
                             <div className="text-sm font-medium text-gray-900">{site.name}</div>
                             <div className="text-xs text-gray-500">{site.code}</div>
                           </div>
                         </div>
                         <div className="flex items-center gap-2">
                           <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                             {site.role}
                           </span>
                           {site.is_default && (
                             <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                               Site par défaut
                             </span>
                           )}
                           <span className="text-xs text-gray-500">
                             Assigné le {new Date(site.assigned_at).toLocaleDateString('fr-FR')}
                           </span>
                         </div>
                       </div>
                     ))}
                   </div>
                 ) : (
                   <div className="text-center py-6">
                     <BuildingOfficeIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                     <p className="text-sm text-gray-500">Aucun site assigné</p>
                     <button 
                       onClick={openAssignSiteModal}
                       className="mt-2 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors"
                     >
                       <PlusIcon className="h-4 w-4" />
                       Assigner un site
                     </button>
                   </div>
                 )}
               </div>
             </div>
          </div>
        )}

        {/* Onglet Historique des connexions */}
        {activeTab === 'connexions' && (
          <div className="mt-6 space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Historique des connexions</h2>
              
              {formattedConnections.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date et heure
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Adresse IP
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Navigateur
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {formattedConnections.map((connexion) => (
                        <tr key={connexion.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-900">
                                {new Date(connexion.date).toLocaleString('fr-FR')}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {connexion.ip}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {connexion.user_agent}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              connexion.status === 'success'
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : 'bg-red-100 text-red-800 border border-red-200'
                            }`}>
                              {connexion.status === 'success' ? 'Succès' : 'Échec'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6">
                  <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune connexion enregistrée</h3>
                  <p className="text-gray-500">Aucune session de connexion n'a été trouvée pour cet utilisateur.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Onglet Historique des actions */}
        {activeTab === 'actions' && (
          <div className="mt-6 space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Historique des actions</h2>
              
              {formattedActions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date et heure
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Action
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Détails
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Adresse IP
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {formattedActions.map((action) => (
                        <tr key={action.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <DocumentTextIcon className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-900">
                                {new Date(action.date).toLocaleString('fr-FR')}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-900">{action.action}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {action.details}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {action.ip}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6">
                  <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune action enregistrée</h3>
                  <p className="text-gray-500">Aucun log d'audit n'a été trouvé pour cet utilisateur.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal de blocage */}
      <Dialog
        isOpen={showBlockDialog}
        onClose={() => setShowBlockDialog(false)}
        title="Bloquer l'utilisateur"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Êtes-vous sûr de vouloir bloquer l'utilisateur <strong>{user?.name}</strong> ?
          </p>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Raison du blocage *
            </label>
            <textarea
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder="Expliquez la raison du blocage..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              rows={3}
              required
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => setShowBlockDialog(false)}
              className="flex-1 rounded-xl px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleBlockUser}
              disabled={!blockReason.trim()}
              className="flex-1 rounded-xl px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              Bloquer l'utilisateur
            </button>
          </div>
        </div>
      </Dialog>

      {/* Modal d'assignation de site */}
      <AssignSiteModal
        isOpen={showAssignSiteModal}
        onClose={() => setShowAssignSiteModal(false)}
        onAssign={handleAssignSite}
        sites={availableSites}
        roles={availableRoles.map(role => ({
          ...role,
          id: String(role.id)
        }))}
        userId={Number(user.id)}
        userName={user.username}
        userEmail={user.email}
        userFirstName={user.name ? user.name.split(' ')[0] : ''}
        userLastName={user.name ? user.name.split(' ').slice(1).join(' ') : ''}
        loading={assigningSite}
      />

      {/* Container des toasts */}
      <ToastContainer toasts={toasts} position="top-right" />
    </div>
  );
};

export default UserDetail;
